import { useState } from 'react';
import { Link } from 'react-router-dom';
import * as videosApi from '../api/videos';
import { usePagedList } from '../lib/usePagedList';
import { useConfirm } from '../lib/confirm';
import { useToast } from '../components/toast';
import { useOrg } from '../lib/org';
import { ErrorAlert, Spinner, EmptyState, PagedListFooter, Field, TextareaField, SelectField, formatDate } from '../components/ui';
import Modal from '../components/Modal';
import { ui, cn } from '../lib/ui';

const PAGE_SIZE = 20;
const AUDIENCE_OPTIONS = ['public', 'registered', 'members', 'group'];

export default function Playlists() {
  const confirm = useConfirm();
  const toast = useToast();
  const { activeOrg } = useOrg();
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', audienceLevel: 'members' });
  const [saving, setSaving] = useState(false);

  const isOrgAdminOrMod = activeOrg && ['org-admin', 'org-moderator'].includes(activeOrg.role);

  const list = usePagedList(
    (cursor) => {
      const query = { limit: PAGE_SIZE };
      if (cursor) query.lastEvaluatedKey = cursor;
      return videosApi.listPlaylists(query)
        .then((d) => ({ items: d.playlists || [], lastEvaluatedKey: d.lastEvaluatedKey }));
    },
    []
  );

  async function onCreate(e) {
    e.preventDefault();
    setSaving(true);
    try {
      await videosApi.createPlaylist(form);
      toast.success('Playlist created.');
      setShowCreate(false);
      setForm({ title: '', description: '', audienceLevel: 'members' });
      list.reload();
    } catch (err) {
      toast.error(err);
    } finally {
      setSaving(false);
    }
  }

  async function onDelete(playlist) {
    const ok = await confirm(`Delete playlist "${playlist.title}"?`);
    if (!ok) return;
    try {
      await videosApi.deletePlaylist(playlist.playlistId);
      toast.success('Playlist deleted.');
      list.removeItem(playlist.playlistId, 'playlistId');
    } catch (err) {
      toast.error(err);
    }
  }

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h1 className="m-0 text-title-l">Playlists</h1>
        {isOrgAdminOrMod && (
          <button className={ui.btnPrimary} onClick={() => setShowCreate(true)}>
            Create playlist
          </button>
        )}
      </div>

      {list.loading && <Spinner />}
      {list.error && <ErrorAlert error={list.error} />}
      {!list.loading && !list.error && list.items.length === 0 && (
        <EmptyState>No playlists yet.</EmptyState>
      )}

      {list.items.length > 0 && (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(17.5rem,1fr))] gap-4">
          {list.items.map((pl) => (
            <div key={pl.playlistId} className={cn(ui.card, 'p-4!')}>
              <h3 className="mt-0 mb-2 text-title-s">
                <Link to={`/videos?playlist=${pl.playlistId}`}>{pl.title}</Link>
              </h3>
              {pl.description && <p className={cn(ui.muted, 'mt-0 mb-2 text-body-s')}>{pl.description}</p>}
              <div className={cn(ui.muted, 'text-caption')}>
                {(pl.videoIds || []).length} video{(pl.videoIds || []).length !== 1 ? 's' : ''} &middot; {pl.audienceLevel} &middot; {formatDate(pl.createdAt)}
              </div>
              {isOrgAdminOrMod && activeOrg.role === 'org-admin' && (
                <button
                  className={cn(ui.btnDanger, ui.btnSm, 'mt-2')}
                  onClick={() => onDelete(pl)}
                >
                  Delete
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      <PagedListFooter
        hasMore={list.hasMore}
        loadingMore={list.loadingMore}
        loadMore={list.loadMore}
        error={list.error}
        reload={list.reload}
      />

      {showCreate && (
        <Modal title="Create playlist" onClose={() => setShowCreate(false)}>
          <form onSubmit={onCreate}>
            <Field label="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required />
            <TextareaField label="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} />
            <SelectField label="Audience" value={form.audienceLevel} onChange={(e) => setForm({ ...form, audienceLevel: e.target.value })}>
              {AUDIENCE_OPTIONS.map((a) => <option key={a} value={a}>{a}</option>)}
            </SelectField>
            <div className="mt-4 flex gap-2">
              <button type="submit" className={ui.btnPrimary} disabled={saving}>{saving ? 'Creating…' : 'Create'}</button>
              <button type="button" className={ui.btnGhost} onClick={() => setShowCreate(false)}>Cancel</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
